import { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { TNullable } from '@common/types';
import { PDF_ZOOM_SCALE } from '@constants/standard-pdf';
import { cn } from '@lib/utils';
import { PDFReadingMode } from '@pages/pdf-viewer/pdf-viewer-shared-props';
import { FileText, PanelRight, ScrollText, ZoomIn } from 'lucide-react';
import { ZoomOut } from 'lucide-react';
import { Redo } from 'lucide-react';
import { Undo } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TChapterInfo } from './pdf.models';

interface IToolbarProps {
  pageInputValue: number | '';
  handleReadingModeChange: (newMode: PDFReadingMode) => void;
  readingMode: PDFReadingMode;
  handlePageChange: (operation: 'prev' | 'next') => void;
  handleSetPage: (e: ChangeEvent<HTMLInputElement>) => void;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  scale: number;
  setPageInputValue: (value: SetStateAction<number | ''>) => void;
  selectedPage: number;
  totalPages: number;
  chapters: TChapterInfo[];
  selectedChapter: TNullable<TChapterInfo>;
  handleChapterChange: (chapterNumber: string) => void;
  shouldShowChapterNavigator: boolean;
  setChapterNavigatorOpen: Dispatch<SetStateAction<boolean>>;
}

function Toolbar(props: IToolbarProps) {
  const { t } = useTranslation();

  return (
    <section className="toolbar fixed right-4 top-24 h-[420px] w-auto bg-xircle-main text-[#eee] opacity-40 border border-gray-600 rounded-md p-3 z-[10] flex flex-col justify-between items-center hover:opacity-100 transition-opacity duration-300">
      <div className="page-controls flex flex-col justify-center items-center gap-2">
        <input
          className="w-7 h-7 text-center font-bold text-black/70 text-xs border border-gray-700 outline-none rounded-full"
          value={props.pageInputValue}
          onChange={props.handleSetPage}
          onBlur={() => props.setPageInputValue(props.selectedPage)}
          disabled={PDFReadingMode.ContinuousPage === props.readingMode}
        />
        <span className="text-xl font-bold border-b-2 border-[#eee]">{props.totalPages}</span>
      </div>
      <div className="pdf-controls flex flex-col justify-between items-center gap-4">
        <div className="pdf-control flex flex-col justify-center items-center gap-1">
          <button
            className="w-9 h-9 flex justify-center items-center cursor-pointer border border-gray-600 rounded-full"
            onClick={() => props.handlePageChange('prev')}
            disabled={props.selectedPage === 1}
            title={t('pdf.prev')}
          >
            <Undo size={20} />
          </button>
          <button
            className="w-9 h-9 flex justify-center items-center cursor-pointer border border-gray-600 rounded-full"
            onClick={() => props.handlePageChange('next')}
            disabled={props.selectedPage === props.totalPages}
            title={t('pdf.next')}
          >
            <Redo size={20} />
          </button>
        </div>
        <div className="pdf-control flex flex-col justify-center items-center gap-1">
          <button
            className="w-9 h-9 flex justify-center items-center cursor-pointer border border-gray-600 rounded-full"
            onClick={props.handleZoomIn}
            disabled={props.scale >= PDF_ZOOM_SCALE.MAX}
            title={t('pdf.zoom_in')}
          >
            <ZoomIn size={20} />
          </button>
          <button
            className="w-9 h-9 flex justify-center items-center cursor-pointer border border-gray-600 rounded-full"
            onClick={props.handleZoomOut}
            disabled={props.scale <= PDF_ZOOM_SCALE.MIN}
            title={t('pdf.zoom_out')}
          >
            <ZoomOut size={20} />
          </button>
        </div>
        <div className="pdf-control flex flex-col justify-center items-center gap-1">
          <button
            className={cn(
              'w-9 h-9 flex justify-center items-center cursor-pointer border border-gray-600 rounded-full',
              PDFReadingMode.SinglePage === props.readingMode && 'bg-slate-100 text-gray-600'
            )}
            onClick={() => props.handleReadingModeChange(PDFReadingMode.SinglePage)}
            title={t('pdf.single_page_mode')}
          >
            <FileText size={20} />
          </button>
          <button
            className={cn(
              'w-9 h-9 flex justify-center items-center cursor-pointer border border-gray-600 rounded-full',
              PDFReadingMode.ContinuousPage === props.readingMode && 'bg-slate-100 text-gray-600'
            )}
            onClick={() => props.handleReadingModeChange(PDFReadingMode.ContinuousPage)}
            title={t('pdf.continuous_page_mode')}
          >
            <ScrollText size={20} />
          </button>
        </div>
      </div>
      {props.shouldShowChapterNavigator && (
        <div
          className="pdf-control flex flex-col justify-center items-center mt-4"
          title={t('pdf.toc')}
        >
          <PanelRight
            fontSize="large"
            className="cursor-pointer"
            onClick={() => props.setChapterNavigatorOpen((prev) => !prev)}
          />
        </div>
      )}
    </section>
  );
}

export default Toolbar;
